import { useEffect, useState } from 'react';
import { isNil } from 'lodash';
import { Search, Info } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { User } from 'lib/types/users';
import { useApi, useCurrentUser } from 'lib/contexts/ApplicationState';
import { Role } from 'lib/types';
import { Credit } from 'lib/types/credits';
import { userIsProOrAdmin } from 'lib/helpers';
import { UserSearch } from 'components/UserSearch';
import { useSelectedUser } from 'components/UserSearch/useSelectedUser';
import { PrivateCheckBox } from 'components/PrivateCheckBox';
import { RolePills } from 'components/RolePills';
import { isSignedUp } from 'lib/redirect';
import { A } from 'components/A';
import { MobileTooltip } from 'components/MobileTooltip';
import { CreditPillButton } from 'components/CreditPillButton';
import { trackClickSignupEvent } from 'lib/hooks/useEventTracking';
import { selfName } from './constants';
import { uniqueCompanies, uniqueUsers } from './utils';
import { RoleHeader } from './RoleHeader';

interface Props {
  postId: number;
  bulk?: boolean;
  credits: Credit[];
  hidePrivate?: boolean;
  disabledSave?: boolean;
  saveLabel?: string;
  onSave: (user: User, roles: Role[], isPrivate?: boolean) => void;
  onSaveAndNext?: (
    user: User, roles: Role[], isPrivate?: boolean
  ) => void;
  onCancel?: () => void;
}

interface UserWithIndex extends User {
  index?: number;
}

export const AddCredits = ({
  saveLabel,
  postId,
  bulk,
  credits,
  hidePrivate,
  disabledSave,
  onSave,
  onSaveAndNext,
  onCancel,
}: Props) => {
  const API = useApi();
  const currentUser = useCurrentUser();

  const [isSaving, setIsSaving] = useState(false);
  const [isUserSearch, setIsUserSearch] = useState(false);
  const [isSuggestionLoading, setIsSuggestionLoading] = useState(false);

  const [users, setUsers] = useState<User[]>([]);

  const [selected, setSelected] = useState<User | undefined>();
  const [selectedRoles, setSelectedRoles] = useState<Role[]>([]);
  const [isPrivate, setIsPrivate] = useState(false);

  // for handling not in db user selection
  const { selectedUser: savedUser, onHandleSelectUser } = useSelectedUser(null);

  const [isFirstCredit, setIsFirstCredit] = useState(credits.length === 0 && !bulk);

  const disable = isSaving || disabledSave || !selected?.id || selectedRoles.length <= 0;

  const save = () => {
    if (!selected?.id || selectedRoles.length <= 0) return;

    setIsSaving(true);
    if (onSave) {
      if (!bulk) {
        onSave(selected as User, selectedRoles, isPrivate);
      } else {
        onSave(selected as User, selectedRoles, isPrivate);
        return;
      }
    }
    setIsSaving(false);
  };

  const saveAndNext = () => {
    if (!selected || selectedRoles.length <= 0) return;

    setIsSaving(true);
    if (onSaveAndNext) {
      onSaveAndNext(selected as User, selectedRoles, isPrivate);
      setSelectedRoles([]);
    }
  };

  const cancel = () => {
    setSelected(undefined);
    setSelectedRoles([]);
    if (onCancel) onCancel();
  };

  const selectUser = (user: UserWithIndex) => {
    if (!isNil(user.index)) {
      const newUsers = [...users];
      newUsers[user.index] = user;
      setUsers(newUsers);
    }
    if (!user.id) {
      onHandleSelectUser(user, (currentUser?.isAdmin || currentUser?.hasPaidTeam));
      return;
    }
    if (user.id === selected?.id) {
      // unselect
      setSelected(undefined);
      setSelectedRoles([]);
      return;
    }

    setSelected(user);

    if (user.defaultRole) {
      setSelectedRoles([user.defaultRole]);
    } else {
      setSelectedRoles([]);
    }
    setIsSaving(false);
  };

  const selectRoles = (roles: Role[]) => {
    setSelectedRoles(roles);
    setIsSaving(false);
  };

  const searchUser = async (user: User) => {
    if (!user || !users) return;

    // Check existing users first
    const existingUser = users.find((u) => u?.id === user.id);
    if (existingUser?.id) {
      selectUser(existingUser);
      setIsUserSearch(false);
      return;
    }

    // Add new user to state and select them
    setUsers((prevUsers) => [user, ...prevUsers]);
    selectUser(user);
    setIsUserSearch(false);
  };

  useEffect(() => {
    if (!savedUser) {
      return;
    }

    // Update to prevent duplicates and ensure consistent state
    setUsers((prevUsers) => {
      const filteredUsers = prevUsers.filter((u) => u.id !== savedUser.id);
      return [savedUser, ...filteredUsers];
    });
    selectUser(savedUser);
    setIsUserSearch(false);
  }, [savedUser]);

  useEffect(() => {
    const showDefaultUsers = (owners: User[]) => {
      if (!currentUser) return;
      /* this is the first credit to select media owner */
      const myself = {
        ...currentUser,
        name: selfName,
      } as User;

      if (!owners?.length) {
        setUsers([myself]);
      } else {
        setUsers([...owners, myself]);
      }
    };

    const selectUserPerCredits = (suggestedUsers: User[]) => {
      if (!currentUser) return;

      const uncreditedUsers = uniqueUsers(suggestedUsers, currentUser.id, 10);

      const proposedUsers = [...uncreditedUsers, currentUser];
      setUsers(proposedUsers as User[]);

      // if current user already has a credit (and is the only user proposed), clear selection.
      if (proposedUsers.length === 1
        && credits.findIndex((c) => c.userId === currentUser.id) >= 0) {
        setSelected(undefined);
        setSelectedRoles([]);
        return;
      }

      selectUser(proposedUsers[0] as User);
    };

    const init = async () => {
      setIsSuggestionLoading(true);
      const suggestedUsers = await API.getSuggestedUserToCredit(postId);

      const firstCredit = (credits.length === 0 && !bulk);
      setIsFirstCredit(firstCredit);
      const possibleOwners = uniqueCompanies(suggestedUsers, currentUser?.id);

      if (firstCredit) {
        showDefaultUsers(possibleOwners as User[]);
      } else {
        selectUserPerCredits(suggestedUsers); // child credit and not edit mode
      }
      setIsSuggestionLoading(false);
    };

    const initBulk = () => {
      setIsFirstCredit(false);

      if (!currentUser || selected) return;

      setUsers([currentUser]);
      selectUser(currentUser);
    };

    if (!bulk) {
      init();
    } else {
      initBulk();
    }
  }, [postId, credits]);

  /**
   * Sometimes it can take a few moments to render suggested users for credits.
   * In order to avoid a weird transition where the UI defaults to asking for a media owner,
   * just return a spinner while loading.
   */
  const renderSuggestionsLoading = () => (
    <div className={`p-4 border-dashed ${!isFirstCredit && 'border-t'}`}>
      <CircularProgress size="2rem" />
    </div>
  );

  const getHeader = () => {
    if (isFirstCredit) return 'Who owns this media?';

    return 'Add a Collaborator';
  };
  if (!isSignedUp(currentUser)) {
    trackClickSignupEvent(API, 'add-credits');
    return (
      <div className="p-4 my-4 text-center border rounded-lg">
        Please finish
        {' '}
        <A href="/signup" className="underline font-semibold">signing up</A>
        {' '}
        before adding credits.
      </div>
    );
  }
  if (isSuggestionLoading) {
    return renderSuggestionsLoading();
  }

  return (
    <div className={`relative py-4 border-dashed ${!isFirstCredit && 'border-t'}`}>
      <h5 className="font-semibold text-xl w-1/2 pb-2">{getHeader()}</h5>
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        {users?.map((u, index) => (
          <CreditPillButton
            user={u}
            index={index}
            // eslint-disable-next-line react/no-array-index-key
            key={`${u.id}-${index}`}
            selected={!!selected?.id && u?.id === selected?.id}
            onClick={(su) => selectUser(su as User)}
          />
        ))}
        <div>
          {!isUserSearch ? (
            <button
              type="button"
              className="border border-mediumgray font-semibold px-2 py-1 rounded-xl hover:bg-lightgray"
              onClick={() => setIsUserSearch(true)}
            >
              <span className="mr-2"><Search fontSize="small" /></span>
              {!users || users.length <= 0 ? 'Search' : 'Other'}
            </button>
          )
            : (
              <div className="w-full min-w-[240px]">
                <UserSearch
                  isCreatableSearch
                  label="Search"
                  onChange={(_: any, value: any) => searchUser(value)}
                  postId={postId}
                  showSearchIcon
                  size="small"
                  value=""
                />
              </div>
            )}
        </div>
      </div>
      {selected
        && (
          <div className="mb-4">
            <RoleHeader name={selected.name} multiple={!isFirstCredit} />
            <RolePills
              user={selected as User}
              selectedRoles={selectedRoles}
              onChange={selectRoles}
              multiple={!isFirstCredit}
            />
          </div>
        )}
      {selected && !hidePrivate && (
        <PrivateCheckBox
          isPrivate={isPrivate}
          onChange={setIsPrivate}
          disabled={!userIsProOrAdmin(currentUser)}
        />
      )}
      <div className="flex flex-wrap gap-2 justify-end">
        <div className="flex w-full sm:w-auto gap-2">
          <button type="button" className="btn-primary-outlined w-1/2 sm:w-auto" onClick={cancel}>Cancel</button>
          <div className="relative w-1/2 sm:w-auto">
            {disable && (
              <MobileTooltip
                placement="top"
                arrow
                className="absolute -top-2 -right-2"
                title="A credit needs an owner/collaborator and a role."
              >
                <Info />
              </MobileTooltip>
            )}

            <button
              type="button"
              className="btn-primary w-full"
              disabled={disable}
              onClick={save}
              data-testid="btn-save"
            >
              {saveLabel || 'Save'}
            </button>
          </div>

        </div>
        {onSaveAndNext && (
          <div className="flex w-full sm:w-auto text-center">
            <button
              type="button"
              className="btn-primary w-full sm:w-auto border-none sm:border-solid"
              disabled={disable}
              onClick={saveAndNext}
            >
              Save & Add Collaborator
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
